/**
 * Accessibility Statement Page
 * WCAG 2.1 Level AA Conformance Information
 */

import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertCircle, Mail, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

interface AccessibilityProps {
  onNavigate?: (page: string) => void;
}

export function Accessibility({ onNavigate }: AccessibilityProps) {
  const handleNavigate = (page: string) => {
    onNavigate?.(page);
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-12 text-center">
            <Badge variant="outline" className="mb-4 gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              WCAG 2.1 Level AA
            </Badge>
            <h1 className="text-4xl sm:text-5xl mb-4">Accessibility Statement</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              CannaBias is committed to ensuring digital accessibility for all users
            </p>
          </div>

          {/* Commitment Card */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Our Commitment</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              CannaBias is committed to ensuring that our platform is accessible to everyone, including people with disabilities. We strive to meet Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards.
            </p>
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Inclusive Design</p>
                  <p className="text-sm text-muted-foreground">Built with accessibility in mind from the ground up</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Continuous Improvement</p>
                  <p className="text-sm text-muted-foreground">Regular audits and updates to maintain standards</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">User Feedback</p>
                  <p className="text-sm text-muted-foreground">We actively welcome and incorporate accessibility feedback</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Standards Compliance */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Standards Compliance</h2>
            <p className="text-muted-foreground mb-6">
              CannaBias conforms to WCAG 2.1 Level AA. This includes:
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-3">Perceivable</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Alternative text for all images</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Sufficient colour contrast (minimum 4.5:1 ratio)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Content adaptable to different presentations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Text resizable up to 200% without loss of functionality</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Operable</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Full keyboard navigation support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Skip navigation links to bypass repetitive content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Sufficient time to read and interact with content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Clear focus indicators for keyboard users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Appropriate touch target sizes (minimum 44×44 pixels)</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Understandable</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Clear and simple language</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Predictable navigation and functionality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Clear error messages with correction suggestions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Labels and instructions for form inputs</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Robust</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Valid HTML markup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Compatible with assistive technologies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                    <span>Appropriate ARIA labels and landmarks</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Assistive Technologies */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Assistive Technology Support</h2>
            <p className="text-muted-foreground mb-6">
              CannaBias has been tested and designed to work with the following assistive technologies:
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Screen Readers</p>
                  <p className="text-sm text-muted-foreground">NVDA, JAWS, VoiceOver</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Keyboard Navigation</p>
                  <p className="text-sm text-muted-foreground">Full site navigation</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Screen Magnification</p>
                  <p className="text-sm text-muted-foreground">ZoomText, Windows Magnifier</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Speech Recognition</p>
                  <p className="text-sm text-muted-foreground">Dragon NaturallySpeaking</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Known Limitations */}
          <Card className="p-8 glass-card mb-8">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-2xl mb-2">Known Limitations</h2>
                <p className="text-muted-foreground">
                  Despite our best efforts, some limitations may exist:
                </p>
              </div>
            </div>

            <ul className="space-y-3 text-muted-foreground ml-9">
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Third-party embedded content (e.g., images uploaded by users) may not always meet accessibility standards</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Some older PDF documents may not be fully accessible</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Complex data visualizations may require alternative descriptions</span>
              </li>
            </ul>

            <div className="mt-6 p-4 bg-accent/30 border border-primary/20 rounded-lg">
              <p className="text-sm text-muted-foreground">
                We are actively working to address these limitations and welcome your feedback on any accessibility barriers you encounter.
              </p>
            </div>
          </Card>

          {/* Feedback */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Feedback & Support</h2>
            <p className="text-muted-foreground mb-6">
              We welcome your feedback on the accessibility of CannaBias. If you encounter any barriers or have suggestions for improvement, please let us know:
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Email</p>
                  <a href="mailto:accessibility@cannabias.tech" className="text-sm text-primary hover:underline">
                    accessibility@cannabias.tech
                  </a>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              We aim to respond to accessibility feedback within 5 business days.
            </p>
          </Card>

          {/* Compliance Date */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Compliance Information</h2>
            <div className="space-y-4 text-muted-foreground">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Standard</p>
                  <p>WCAG 2.1 Level AA</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Conformance Status</p>
                  <Badge variant="default" className="bg-primary">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Conformant
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Last Reviewed</p>
                  <p>November 2025</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Next Review</p>
                  <p>February 2026</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Assessment Methods */}
          <Card className="p-8 glass-card mb-8">
            <h2 className="text-2xl mb-4">Assessment Methods</h2>
            <p className="text-muted-foreground mb-4">
              This accessibility statement is based on the following assessment methods:
            </p>
            
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>Self-evaluation using automated tools (axe DevTools, Lighthouse)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>Manual testing with keyboard navigation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>Screen reader testing (NVDA, VoiceOver)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>User testing with individuals who use assistive technologies</span>
              </li>
            </ul>
          </Card>

          {/* Legal Information */}
          <Card className="p-8 glass-card">
            <h2 className="text-2xl mb-4">Legal Information</h2>
            <p className="text-muted-foreground mb-4">
              CannaBias is committed to complying with the following legislation:
            </p>
            
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>UK Equality Act 2010</span>
              </li>
              <li className="flex items-start gap-2">
                <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>Public Sector Bodies (Websites and Mobile Applications) Accessibility Regulations 2018</span>
              </li>
              <li className="flex items-start gap-2">
                <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                <span>Web Content Accessibility Guidelines (WCAG) 2.1</span>
              </li>
            </ul>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                This statement was prepared on November 5, 2025 and last updated on November 5, 2025.
              </p>
            </div>
          </Card>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button
              onClick={() => handleNavigate("home")}
              variant="outline"
              className="gap-2"
            >
              Return to Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
